import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, defer, EMPTY, Observable, Subject } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { TALK_TO_YOUR_DATA_HISTORY_MAX_ITEMS } from '../constants/chatbot.constants';
import { ChatbotMapper } from '../data/mappers/chatbot.mapper';
import { ConversationStatus } from '../domain/enums/conversation-status.enum';
import { MessageBlockType } from '../domain/enums/message-block-type.enum';
import { MessageRole } from '../domain/enums/message-role.enum';
import { ChatConversation } from '../domain/models/chat-conversation.model';
import { ChatMessage } from '../domain/models/chat-message.model';
import { ConversationSummary } from '../domain/models/conversation-summary.model';
import { QuickQuestion } from '../domain/models/quick-question.model';
import { UserProfile } from '../domain/models/user-profile.model';
import { SimulatedResponseFactory } from '../domain/services/simulated-response.factory';
import { ChatbotApiService } from '../infrastructure/api/chatbot.api.service';
import { ConversationHistoryStorageAdapter } from '../infrastructure/adapters/conversation-history-storage.adapter';
import { ChatbotUserContextAdapter, ChatbotUserContext } from '../infrastructure/adapters/chatbot-user-context.adapter';
import { ChatRequestDto } from '../infrastructure/dto/chat-request.dto';
import { ChatResponseDto } from '../infrastructure/dto/chat-response.dto';
import { ConversationRestoreResponseDto } from '../infrastructure/dto/conversation-restore-response.dto';
import {
  initializeConversationState,
  resetConversationState,
  setActiveConversation,
  setConversationHistory,
  setSavedConversations,
  setSelectedConversationId,
  setError,
  setLoading,
  setQuickQuestions,
  setSendingMessage,
} from '../state/conversation/conversation.actions';
import {
  selectActiveConversation,
  selectConversationHistory,
  selectError,
  selectLoading,
  selectQuickQuestions,
  selectSavedConversations,
  selectSelectedConversationId,
  selectSendingMessage,
} from '../state/conversation/conversation.selectors';
import { ConversationState } from '../state/conversation/conversation.state';

const UNKNOWN_INITIALS = '?';
const UNKNOWN_USER_NAME = 'Unknown User';
const NO_ROLE_ASSIGNED = 'QC Manager · Houston Plant';
const REQUEST_CANCELLED_MESSAGE = 'Request cancelled by the user';

@Injectable()
export class ChatbotFacade implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  readonly activeConversation$: Observable<ChatConversation | null> = this.store.select(selectActiveConversation);
  readonly conversationHistory$: Observable<readonly ConversationSummary[]> =
    this.store.select(selectConversationHistory);
  readonly selectedConversationId$: Observable<string | null> = this.store.select(selectSelectedConversationId);
  readonly quickQuestions$: Observable<readonly QuickQuestion[]> = this.store.select(selectQuickQuestions);
  readonly loading$: Observable<boolean> = this.store.select(selectLoading);
  readonly sendingMessage$: Observable<boolean> = this.store.select(selectSendingMessage);
  readonly error$: Observable<string | null> = this.store.select(selectError);
  readonly savedConversations$: Observable<readonly ConversationSummary[]> =
    this.store.select(selectSavedConversations);

  readonly userProfile$: Observable<UserProfile> = defer(() =>
    this.chatbotUserContextAdapter.getUserContext$().pipe(
      map((context) => ({
        initials: this.buildInitials(context),
        fullName: context.fullName?.trim() || UNKNOWN_USER_NAME,
        role: context.role?.trim() || NO_ROLE_ASSIGNED,
      })),
    ),
  );

  constructor(
    private readonly store: Store<ConversationState>,
    private readonly conversationHistoryStorageAdapter: ConversationHistoryStorageAdapter,
    private readonly chatbotApiService: ChatbotApiService,
    private readonly chatbotMapper: ChatbotMapper,
    private readonly chatbotUserContextAdapter: ChatbotUserContextAdapter,
  ) {}

  private readonly simulatedResponseFactory = new SimulatedResponseFactory();

  // Emite para abortar la petición HTTP del chat en curso.
  private readonly cancelAssistantRequest$ = new Subject<void>();
  private readonly cancelConversationRestore$ = new Subject<void>();

  /**
   * Aborts the in-flight Chat API request, replaces the pending assistant bubble
   * with a cancellation notice and re-enables the message input.
   */
  cancelAssistantRequest(): void {
    this.cancelAssistantRequest$.next();
    this.replacePendingAssistantMessages();
    this.setSendingMessage(false);
    this.setError(null);
  }

  ngOnDestroy(): void {
    this.cancelConversationRestore$.next();
    this.cancelConversationRestore$.complete();
    this.cancelAssistantRequest$.next();
    this.cancelAssistantRequest$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private replacePendingAssistantMessages(): void {
    this.activeConversation$.pipe(take(1), takeUntil(this.destroy$)).subscribe((activeConversation) => {
      if (!activeConversation) {
        return;
      }

      const hasPending = activeConversation.messages.some((message) => message.pending);

      if (!hasPending) {
        return;
      }

      const messages = activeConversation.messages.map((message) =>
        message.pending ? this.buildCancelledAssistantMessage(activeConversation.id, message.id) : message,
      );

      this.setActiveConversation({ ...activeConversation, messages });
    });
  }

  private buildCancelledAssistantMessage(conversationId: string, messageId: string): ChatMessage {
    return {
      id: messageId,
      conversationId,
      role: MessageRole.ASSISTANT,
      createdAt: new Date().toISOString(),
      blocks: [
        {
          id: this.generateId('block-assistant-cancelled'),
          type: MessageBlockType.TEXT,
          text: REQUEST_CANCELLED_MESSAGE,
        },
      ],
    };
  }

  initializeConversationState(): void {
    this.store.dispatch(initializeConversationState());
  }

  resetConversationState(): void {
    this.cancelConversationRestore$.next();
    this.store.dispatch(resetConversationState());
  }

  setActiveConversation(conversation: ChatConversation | null): void {
    this.store.dispatch(setActiveConversation({ activeConversation: conversation }));
  }

  setConversationHistory(history: readonly ConversationSummary[]): void {
    this.saveConversationHistory(history);
  }

  loadConversationHistory(): void {
    const history = this.limitHistory(this.conversationHistoryStorageAdapter.loadHistory());
    this.store.dispatch(setConversationHistory({ conversationHistory: history }));
  }

  saveConversationHistory(history: readonly ConversationSummary[]): void {
    const limitedHistory = this.limitHistory(history);
    this.conversationHistoryStorageAdapter.saveHistory(limitedHistory);
    this.store.dispatch(setConversationHistory({ conversationHistory: limitedHistory }));
  }

  loadSavedConversations(): void {
    const savedConversations = this.conversationHistoryStorageAdapter.loadSavedConversations();
    this.store.dispatch(setSavedConversations({ savedConversations }));
  }

  saveSavedConversations(savedConversations: readonly ConversationSummary[]): void {
    this.conversationHistoryStorageAdapter.saveSavedConversations(savedConversations);
    this.store.dispatch(setSavedConversations({ savedConversations }));
  }

  /** Moves a conversation from history to the top of saved conversations. */
  saveConversation(conversationId: string): void {
    combineLatest([this.conversationHistory$, this.savedConversations$])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([history, savedConversations]) => {
        const summary = history.find((item) => this.matchesConversation(item, conversationId));

        if (!summary) {
          return;
        }

        // Saved list is updated first so the reducer keeps the current selection.
        this.saveSavedConversations([summary, ...savedConversations]);
        this.saveConversationHistory(history.filter((item) => !this.matchesConversation(item, conversationId)));
      });
  }

  /** Moves a saved conversation back to the first position of history, as a new entry. */
  unsaveConversation(conversationId: string): void {
    combineLatest([this.conversationHistory$, this.savedConversations$])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([history, savedConversations]) => {
        const summary = savedConversations.find((item) => this.matchesConversation(item, conversationId));

        if (!summary) {
          return;
        }

        // History is updated first so the reducer keeps the current selection.
        this.saveConversationHistory([
          summary,
          ...history.filter((item) => !this.matchesConversation(item, conversationId)),
        ]);
        this.saveSavedConversations(
          savedConversations.filter((item) => !this.matchesConversation(item, conversationId)),
        );
      });
  }

  renameConversation(conversationId: string, title: string): void {
    const normalizedTitle = (title || '').trim();

    if (!conversationId || !normalizedTitle) {
      return;
    }

    combineLatest([this.conversationHistory$, this.savedConversations$, this.activeConversation$])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([history, savedConversations, activeConversation]) => {
        const renamedSummary = (summary: ConversationSummary): ConversationSummary =>
          this.matchesConversation(summary, conversationId) ? { ...summary, title: normalizedTitle } : summary;

        if (history.some((item) => this.matchesConversation(item, conversationId))) {
          this.saveConversationHistory(history.map(renamedSummary));
        }

        if (savedConversations.some((item) => this.matchesConversation(item, conversationId))) {
          this.saveSavedConversations(savedConversations.map(renamedSummary));
        }

        if (activeConversation?.id === conversationId) {
          this.setActiveConversation({ ...activeConversation, title: normalizedTitle });
        }
      });
  }

  deleteConversation(conversationId: string): void {
    if (!conversationId) {
      return;
    }

    combineLatest([
      this.conversationHistory$,
      this.savedConversations$,
      this.activeConversation$,
      this.selectedConversationId$,
    ])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([history, savedConversations, activeConversation, selectedConversationId]) => {
        this.saveConversationHistory(history.filter((item) => !this.matchesConversation(item, conversationId)));
        this.saveSavedConversations(
          savedConversations.filter((item) => !this.matchesConversation(item, conversationId)),
        );

        if (activeConversation?.id === conversationId) {
          this.setActiveConversation(null);
        }

        if (selectedConversationId === conversationId) {
          this.selectConversation(null);
        }
      });
  }

  clearConversationHistory(): void {
    this.conversationHistoryStorageAdapter.clearHistory();
    this.store.dispatch(setConversationHistory({ conversationHistory: [] }));
    this.selectConversation(null);
  }

  selectConversation(conversationId: string | null): void {
    this.cancelConversationRestore$.next();
    this.setLoading(false);
    this.store.dispatch(setSelectedConversationId({ selectedConversationId: conversationId }));
  }

  /** Entry point for reopening a conversation from history. */
  openConversation(conversationId: string): void {
    if (!conversationId) {
      return;
    }

    this.selectConversation(conversationId);
    this.setActiveConversation(null);
    this.setLoading(true);
    this.setError(null);

    combineLatest([this.conversationHistory$, this.savedConversations$])
      .pipe(
        take(1),
        map(([history, savedConversations]) =>
          [...savedConversations, ...history].find((item) => this.matchesConversation(item, conversationId)),
        ),
        switchMap((summary) => {
          if (!summary) {
            this.setError('conversation-restore-unavailable');
            this.setLoading(false);
            return EMPTY;
          }

          return this.chatbotApiService.getConversation(conversationId).pipe(
            take(1),
            map((response: ConversationRestoreResponseDto) => ({ response, summary })),
            catchError(() => {
              this.setError('conversation-restore-unavailable');
              this.setLoading(false);
              return EMPTY;
            }),
          );
        }),
        takeUntil(this.cancelConversationRestore$),
        takeUntil(this.destroy$),
      )
      .subscribe(({ response, summary }) => {
        this.setActiveConversation(this.chatbotMapper.toConversation(response, summary));
        this.setLoading(false);
      });
  }

  setQuickQuestions(quickQuestions: readonly QuickQuestion[]): void {
    this.store.dispatch(setQuickQuestions({ quickQuestions }));
  }

  setLoading(loading: boolean): void {
    this.store.dispatch(setLoading({ loading }));
  }

  setSendingMessage(sending: boolean): void {
    this.store.dispatch(setSendingMessage({ sendingMessage: sending }));
  }

  setError(error: string | null): void {
    this.store.dispatch(setError({ error }));
  }

  loadConversation(): void {
    // TODO: Wire async load flow in a follow-up step.
    return;
  }

  /**
   * Creates the user message, calls the Chat API and appends the mapped assistant message.
   * Falls back to SimulatedResponseFactory when the API fails (temporary, see technical debt).
   */
  sendMessage(prompt: string, conversationTitle?: string): void {
    const question = (prompt || '').trim();

    if (!question) {
      return;
    }

    combineLatest([this.activeConversation$, this.conversationHistory$])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([activeConversation, history]) => {
        const conversation = this.appendUserMessage(activeConversation, history, question, conversationTitle);
        this.requestAssistantResponse(conversation, question);
      });
  }

  sendQuickQuestion(question: QuickQuestion): void {
    this.sendMessage(question.prompt, question.title);
  }

  private appendUserMessage(
    activeConversation: ChatConversation | null,
    history: readonly ConversationSummary[],
    question: string,
    conversationTitle?: string,
  ): ChatConversation {
    const nowIso = new Date().toISOString();
    const conversationId = activeConversation?.id || this.generateId('conversation');

    const userMessage: ChatMessage = {
      id: this.generateId('message-user'),
      conversationId,
      role: MessageRole.USER,
      createdAt: nowIso,
      blocks: [
        {
          id: this.generateId('block-user-text'),
          type: MessageBlockType.TEXT,
          text: question,
        },
      ],
    };

    if (!activeConversation) {
      const newConversation: ChatConversation = {
        id: conversationId,
        title: conversationTitle || question.substring(0, 50),
        createdAt: nowIso,
        updatedAt: nowIso,
        messages: [userMessage],
        status: ConversationStatus.ACTIVE,
      };

      this.setActiveConversation(newConversation);
      this.selectConversation(conversationId);
      this.saveConversationHistory(
        this.upsertConversationSummary(history, {
          conversationId,
          title: newConversation.title,
          previewText: question,
          lastActivityDate: nowIso,
        }),
      );

      return newConversation;
    }

    const updatedConversation: ChatConversation = {
      ...activeConversation,
      messages: [...activeConversation.messages, userMessage],
      updatedAt: nowIso,
    };

    this.setActiveConversation(updatedConversation);

    const existingSummary = history.find((item) => this.matchesConversation(item, conversationId));

    if (existingSummary) {
      this.saveConversationHistory([
        { ...existingSummary, previewText: question, lastActivityDate: nowIso },
        ...history.filter((item) => !this.matchesConversation(item, conversationId)),
      ]);
    }

    return updatedConversation;
  }

  private requestAssistantResponse(conversation: ChatConversation, question: string): void {
    const userContext = this.chatbotUserContextAdapter.getUserContext();
    const request: Omit<ChatRequestDto, 'role'> = {
      question,
      username: userContext.fullName,
      conversation_id: conversation.id,
      email: userContext.email,
    };

    this.setSendingMessage(true);
    this.setError(null);

    // Placeholder bubble keeps the same id as the final message to avoid a re-mount on replace.
    const assistantMessageId = this.generateId('message-assistant');
    this.appendPendingAssistantMessage(conversation.id, assistantMessageId);

    this.chatbotUserContextAdapter
      .getRoleCode$()
      .pipe(
        take(1),
        switchMap((role) => {
          return this.chatbotApiService.sendMessage({ ...request, role });
        }),
      )
      .pipe(take(1), takeUntil(this.cancelAssistantRequest$), takeUntil(this.destroy$))
      .subscribe({
        next: (response: ChatResponseDto) => {
          const assistantMessage = response?.answer
            ? this.chatbotMapper.toAssistantMessage(
                response,
                conversation.id,
                assistantMessageId,
                this.generateId('block-assistant'),
              )
            : this.buildFallbackAssistantMessage(conversation.id, assistantMessageId);

          this.resolveAssistantMessage(conversation.id, assistantMessage, question);
          this.setSendingMessage(false);
        },
        error: () => {
          this.setError('chat-api-unavailable');
          this.resolveAssistantMessage(
            conversation.id,
            this.buildFallbackAssistantMessage(conversation.id, assistantMessageId),
            question,
          );
          this.setSendingMessage(false);
        },
      });
  }

  private appendPendingAssistantMessage(conversationId: string, messageId: string): void {
    this.activeConversation$.pipe(take(1), takeUntil(this.destroy$)).subscribe((activeConversation) => {
      if (!activeConversation || activeConversation.id !== conversationId) {
        return;
      }

      const pendingMessage: ChatMessage = {
        id: messageId,
        conversationId,
        role: MessageRole.ASSISTANT,
        createdAt: new Date().toISOString(),
        blocks: [],
        pending: true,
      };

      this.setActiveConversation({
        ...activeConversation,
        messages: [...activeConversation.messages, pendingMessage],
      });
    });
  }

  private buildFallbackAssistantMessage(conversationId: string, messageId: string): ChatMessage {
    return {
      id: messageId,
      conversationId,
      role: MessageRole.ASSISTANT,
      createdAt: new Date().toISOString(),
      blocks: [
        {
          id: this.generateId('block-assistant-text'),
          type: MessageBlockType.TEXT,
          text: this.simulatedResponseFactory.generateResponse(),
        },
      ],
    };
  }

  private resolveAssistantMessage(conversationId: string, assistantMessage: ChatMessage, question: string): void {
    combineLatest([this.activeConversation$, this.conversationHistory$, this.savedConversations$])
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(([activeConversation, history, savedConversations]) => {
        if (!activeConversation || activeConversation.id !== conversationId) {
          return;
        }

        const nowIso = new Date().toISOString();
        const hasPlaceholder = activeConversation.messages.some((message) => message.id === assistantMessage.id);
        const messages = hasPlaceholder
          ? activeConversation.messages.map((message) =>
              message.id === assistantMessage.id ? assistantMessage : message,
            )
          : [...activeConversation.messages, assistantMessage];

        this.setActiveConversation({
          ...activeConversation,
          messages,
          updatedAt: nowIso,
        });

        const summary: ConversationSummary = {
          conversationId,
          title: activeConversation.title,
          previewText: question,
          lastActivityDate: nowIso,
        };

        if (savedConversations.some((item) => this.matchesConversation(item, conversationId))) {
          this.saveSavedConversations(this.upsertConversationSummary(savedConversations, summary));
          return;
        }

        this.saveConversationHistory(this.upsertConversationSummary(history, summary));
      });
  }

  // Oldest entries sit at the end because new conversations are always prepended.
  private limitHistory(history: readonly ConversationSummary[]): readonly ConversationSummary[] {
    return history.slice(0, TALK_TO_YOUR_DATA_HISTORY_MAX_ITEMS);
  }

  private matchesConversation(summary: ConversationSummary, conversationId: string): boolean {
    return summary.conversationId === conversationId || summary.id === conversationId;
  }

  private upsertConversationSummary(
    history: readonly ConversationSummary[],
    summary: ConversationSummary,
  ): readonly ConversationSummary[] {
    const existingIndex = history.findIndex((historyItem) =>
      this.matchesConversation(historyItem, summary.conversationId),
    );

    if (existingIndex === -1) {
      return [summary, ...history];
    }

    return history.map((historyItem, index) =>
      index === existingIndex
        ? { ...historyItem, previewText: summary.previewText, lastActivityDate: summary.lastActivityDate }
        : historyItem,
    );
  }

  private generateId(prefix: string): string {
    return prefix + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
  }

  /** Prefers Name/LastName from idb accountInfo; falls back to the first two tokens of the full name. */
  private buildInitials(context: ChatbotUserContext): string {
    const fromAccount = [context.firstName, context.lastName]
      .filter(Boolean)
      .map((name) => (name as string).charAt(0).toUpperCase())
      .join('');

    if (fromAccount) {
      return fromAccount;
    }

    const tokens = (context.fullName || '').trim().split(/\s+/).filter(Boolean);

    const initials = tokens
      .slice(0, 2)
      .map((token) => token.charAt(0).toUpperCase())
      .join('');

    return initials || UNKNOWN_INITIALS;
  }

  createConversation(): void {
    // TODO: Wire async create flow in a follow-up step.
    return;
  }

  loadQuickQuestions(): void {
    // TODO: Wire async quick-questions flow in a follow-up step.
    return;
  }
}
